import { GoogleGenerativeAI } from "@google/generative-ai";

// --- Daily message usage tracker (localStorage-based) ---
const STORAGE_KEY = "brokerinsight_chat_usage";
export const DAILY_MESSAGE_LIMIT = 20;

const getTodayKey = () => new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

export const getMessageUsage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { count: 0, date: getTodayKey() };
    const parsed = JSON.parse(raw);
    // Reset if it's a new day
    if (parsed.date !== getTodayKey()) return { count: 0, date: getTodayKey() };
    return parsed;
  } catch {
    return { count: 0, date: getTodayKey() };
  }
};

export const incrementMessageUsage = () => {
  const usage = getMessageUsage();
  const updated = { count: usage.count + 1, date: getTodayKey() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

export const hasReachedDailyLimit = () =>
  getMessageUsage().count >= DAILY_MESSAGE_LIMIT;

export const getAgentInsights = async (agent, settingsApiKey) => {
  // Use settings key if provided, fallback to environment
  const apiKey =
    settingsApiKey ||
    process.env.REACT_APP_GEMINI_API_KEY ||
    process.env.API_KEY;

  if (!apiKey) {
    return "API Key non configurata nelle Impostazioni dell'Engine.";
  }

  try {
    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });

    const prompt = `
      Sei un Senior Business Analyst esperto in assicurazioni. Analizza le performance di questo agente e spiega perché non sta ottenendo il bonus massimo o come può migliorare.
      Dati Agente:
      - Nome: ${agent.name}
      - Ritenzione: ${agent.retentionRate.toFixed(
        1,
      )}% (Target Base: 70%, Super: 80%)
      - Indice Cross-Selling: ${agent.mixNonMotor.toFixed(2)}% (Target: 1.3%)
      - Garanzie Medie (Nuove): ${agent.avgGarMotorNew.toFixed(1)} (Target: 2.0)
      - Garanzie Medie (Rinnovi): ${agent.avgGarMotorRen.toFixed(
        1,
      )} (Target: 1.1)
      - Livello Bonus Attuale: ${agent.bonusLevel}
      
      Rispondi in italiano in modo incoraggiante ma professionale. Sii molto sintetico e specifico sui numeri. Usa un formato elenco puntato.
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text() || "Approfondimento non disponibile.";
  } catch (error) {
    console.error("Gemini Error:", error);
    if (error.message?.includes("API_KEY_INVALID")) {
      return "Chiave API Gemini non valida. Verificare le Impostazioni.";
    }
    return "Errore nella generazione dell'insight AI.";
  }
};

export const getBrokerInsightsChat = async (kpis, criticalRecords, userMsg) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY || process.env.API_KEY;
  if (!apiKey) return "API Key non configurata.";

  try {
    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-lite" });

    const prompt = `
      Sei un Senior Broker Advisor esperto in ritenzione e analisi portafoglio.
      Analizza questi dati della dashboard BrokerInsight e rispondi alla domanda dell'utente.

      KPI Dashboard:
      - Totale Premi Rinnovo: €${kpis.totalPremiumRenewal.toLocaleString(
        "it-IT",
      )}
      - Delta Assoluto: €${kpis.totalDeltaAbsolute.toLocaleString("it-IT")}
      - Aumento Medio Portafoglio: ${kpis.avgDeltaPercent.toFixed(1)}%
      - Posizioni Critiche (>20%): ${kpis.countIncrease20}
      - Valore a Rischio: €${kpis.valueAtRiskHigh.toLocaleString("it-IT")}

      Top Record Critici:
      ${criticalRecords
        .map(
          (r) =>
            `- ${r.clienteRinnovo} (${r.prodottoRinnovo}): Delta ${Math.round(
              r.deltaPercentualeCalcolato,
            )}% (Premi: €${r.premioAnnualeScadenza} -> €${
              r.premioAnnualeRinnovo
            })`,
        )
        .join("\n")}

      Domanda dell'utente: "${userMsg}"
      
      Rispondi in italiano in modo professionale, sintetico e basandoti sui dati forniti. 
      Suggerisci azioni concrete per mitigare il rischio di abbandono.
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text() || "Assenza di risposta dall'AI.";
  } catch (error) {
    console.error("Gemini Error:", error);
    return "Errore nella comunicazione con l'assistente AI.";
  }
};
